<template>
  <div class="mask" :class="{ 'mask-hidden': isSubDialogOpen }" @mousedown.self="onCancel">
    <div v-show="!isSubDialogOpen" class="grouping-dialog">
      <!-- ヘッダー -->
      <div class="grouping-header">
        <span class="grouping-header-edit" @click="onClickEdit">{{ $t("homePage.novelList.grouping.edit") }}</span>
        <span class="grouping-header-title">{{ $t("homePage.novelList.grouping.title") }}</span>
        <button class="grouping-close-button" @click="onCancel">
          <IconClose class="grouping-close-icon" />
        </button>
      </div>

      <!-- 検索 -->
      <div class="grouping-search">
        <IconSearch class="grouping-search-icon" />
        <input
          v-model="searchQuery"
          class="grouping-search-input"
          type="text"
          :placeholder="$t('homePage.novelList.grouping.searchPlaceholder')"
        />
      </div>

      <div class="grouping-body">
        <!-- 空状態 -->
        <template v-if="groups.length === 0">
          <div class="grouping-empty">
            <p class="grouping-empty-message">{{ $t("homePage.novelList.grouping.emptyMessage") }}</p>
            <button class="grouping-create-button" @click="onClickCreateGroup">
              {{ $t("homePage.novelList.grouping.createNew") }}
            </button>
          </div>
        </template>

        <!-- グループ一覧 -->
        <template v-else>
          <!-- チップ一覧 -->
          <div class="grouping-chips">
            <!-- 追加ボタン -->
            <button class="grouping-add-button" @click="onClickCreateGroup">
              <IconPlus class="grouping-add-icon" />
            </button>
            <button
              v-for="group in filteredGroups"
              :key="group.groupId"
              class="grouping-chip"
              :class="{ active: selectedGroupIds.includes(group.groupId) }"
              @click="toggleGroup(group.groupId)"
            >
              {{ group.name }}
            </button>
          </div>
        </template>
      </div>

      <!-- アクション -->
      <div class="grouping-actions">
        <button class="grouping-apply-button" @click="onApply">
          {{ $t("homePage.novelList.grouping.apply") }}
        </button>
        <button class="grouping-cancel-button" @click="onCancel">
          {{ $t("common.cancel") }}
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import { NovelGroup } from "@/lib/models";
import { Dialog } from "@/lib/utils";
import SimpleDialog, { SimpleDialogProps } from "@/components/ui/dialogs/SimpleDialog.vue";
import NovelGroupAddDialog, { DIALOG_RESULT_PREMIUM } from "./NovelGroupAddDialog.vue";
import NovelGroupEditDialog from "./NovelGroupEditDialog.vue";
import IconSearch from "~icons/figma/Search";
import IconPlus from "~icons/figma/Plus";
import IconClose from "~icons/figma/Close";

export interface NovelGroupingDialogProps {
  novelId: string;
  currentGroupIds: string[];
}

export default Vue.extend<Data, Methods, Computed, Props>({
  components: { IconSearch, IconPlus, IconClose },
  props: {
    novelId: {
      type: String,
      required: true,
    },
    currentGroupIds: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      searchQuery: "",
      selectedGroupIds: [...(this.currentGroupIds as string[])],
      isSubDialogOpen: false,
    };
  },

  computed: {
    groups() {
      return this.$store.getters["novelGroupModule/groups"] as NovelGroup[];
    },
    filteredGroups() {
      if (!this.searchQuery) return this.groups;
      // macOS 由来の NFD グループ名でも検索がヒットするよう両辺を NFC 正規化して比較する。
      const normalizedQuery = this.searchQuery.normalize("NFC");
      return this.groups.filter((g) => g.name.normalize("NFC").includes(normalizedQuery));
    },
  },

  methods: {
    toggleGroup(groupId: string) {
      const index = this.selectedGroupIds.indexOf(groupId);
      if (index >= 0) {
        this.selectedGroupIds.splice(index, 1);
      } else {
        this.selectedGroupIds.push(groupId);
      }
    },

    async onClickEdit() {
      this.isSubDialogOpen = true;
      const editDialog = new Dialog(NovelGroupEditDialog);
      const result = await editDialog.show({});
      if (result === DIALOG_RESULT_PREMIUM) {
        this.$close(null);
        return;
      }
      this.isSubDialogOpen = false;
    },

    async onClickCreateGroup() {
      this.isSubDialogOpen = true;
      const addDialog = new Dialog(NovelGroupAddDialog);
      const result = await addDialog.show({});
      if (result === DIALOG_RESULT_PREMIUM) {
        this.$close(null);
        return;
      }
      this.isSubDialogOpen = false;
      if (result) {
        const created = result as NovelGroup;
        this.selectedGroupIds.push(created.groupId);
      }
    },

    async onApply() {
      const { novelId, currentGroupIds, selectedGroupIds, groups } = this;

      const addedIds = selectedGroupIds.filter((id) => !(currentGroupIds as string[]).includes(id));
      const removedIds = (currentGroupIds as string[]).filter((id) => !selectedGroupIds.includes(id));

      const addPromises = addedIds
        .map((groupId) => {
          const group = groups.find((g) => g.groupId === groupId);
          if (!group) return null;
          const novelKeys = [...group.novelKeys, novelId];
          return this.$store.dispatch("novelGroupModule/updateGroup", { groupId, novelKeys });
        })
        .filter(Boolean);

      const removePromises = removedIds
        .map((groupId) => {
          const group = groups.find((g) => g.groupId === groupId);
          if (!group) return null;
          const novelKeys = group.novelKeys.filter((key: string) => key !== novelId);
          return this.$store.dispatch("novelGroupModule/updateGroup", { groupId, novelKeys });
        })
        .filter(Boolean);

      const allPromises = [...addPromises, ...removePromises];
      if (allPromises.length === 0) {
        this.$close(null);
        return;
      }

      try {
        await Promise.all(allPromises);
        this.$close(true);
      } catch (e) {
        const errorDialog = new Dialog(SimpleDialog);
        const errorData: SimpleDialogProps = {
          title: this.$t("homePage.novelList.grouping.errorTitle") as string,
          content: this.$t("homePage.novelList.grouping.errorMessage") as string,
          isError: true,
          width: 350,
        };
        errorDialog.show(errorData);
      }
    },

    onCancel() {
      this.$close(null);
    },
  },
});

interface Props {
  novelId: string;
  currentGroupIds: string[];
}

interface Data {
  searchQuery: string;
  selectedGroupIds: string[];
  isSubDialogOpen: boolean;
}

interface Computed {
  groups: NovelGroup[];
  filteredGroups: NovelGroup[];
}

interface Methods {
  toggleGroup(groupId: string): void;
  onClickEdit(): Promise<void>;
  onClickCreateGroup(): Promise<void>;
  onApply(): Promise<void>;
  onCancel(): void;
}
</script>

<style lang="scss" scoped>
.mask {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  z-index: 8000;
  align-items: center;
  justify-content: center;
  background-color: var(--color-overlay);

  &.mask-hidden {
    background-color: transparent;
  }
}

.grouping-dialog {
  background-color: var(--color-background-normal);
  border-radius: var(--radius-md);
  width: 340px;
  max-width: 90%;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
}

/* ヘッダー */
.grouping-header {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  padding: 20px 24px 12px;
}

.grouping-header-edit {
  position: absolute;
  left: 24px;
  font-size: var(--font-size-sm);
  color: var(--color-primary);
  cursor: pointer;
}

.grouping-header-title {
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-normal);
}

.grouping-close-button {
  position: absolute;
  right: 16px;
  top: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
}

.grouping-close-icon {
  font-size: var(--font-size-sm);
  color: var(--color-text-normal);
}

/* 空状態 */
.grouping-empty {
  padding: 24px 0;
  text-align: center;
}

.grouping-empty-message {
  font-size: var(--font-size-sm);
  color: var(--color-text-low);
  margin-bottom: 16px;
}

.grouping-create-button {
  display: inline-block;
  padding: 8px 20px;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-normal);
  background-color: var(--color-surface-muted);
  border: none;
  border-radius: var(--radius-rounded);
  cursor: pointer;
  transition: opacity 0.15s;

  &:hover {
    opacity: 0.8;
  }
}

/* 検索 */
.grouping-search {
  display: flex;
  align-items: center;
  margin: 0 24px;
  padding: 8px 12px;
  background-color: var(--color-surface-muted);
  border-radius: var(--radius-sm);
  gap: 8px;
}

.grouping-search-icon {
  font-size: var(--font-size-md);
  color: var(--color-text-low);
  flex-shrink: 0;
}

.grouping-search-input {
  flex: 1;
  border: none;
  background: transparent;
  font-size: var(--font-size-sm);
  color: var(--color-text-normal);
  outline: none;

  &::placeholder {
    color: var(--color-inactive);
  }
}

/* ボディ */
.grouping-body {
  flex: 1;
  overflow-y: auto;
  padding: 12px 24px 0;
}

/* 追加ボタン */
.grouping-add-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: var(--radius-rounded);
  background-color: var(--color-surface-muted);
  cursor: pointer;
  color: var(--color-text-normal);
  flex-shrink: 0;

  &:hover {
    opacity: 0.7;
  }
}

.grouping-add-icon {
  font-size: var(--font-size-sm);
}

/* チップ */
.grouping-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.grouping-chip {
  padding: 7px 16px;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-bold);
  border: none;
  border-radius: var(--radius-rounded);
  background-color: var(--color-surface-muted);
  color: var(--color-text-normal);
  cursor: pointer;
  transition: background-color 0.15s, color 0.15s;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:hover {
    opacity: 0.8;
  }

  &.active {
    background-color: var(--color-background-invert);
    color: var(--color-text-invert);
  }
}

/* アクション */
.grouping-actions {
  padding: 12px 24px 20px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.grouping-apply-button {
  width: 100%;
  padding: 10px;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-on-fixed-bg);
  background-color: var(--color-primary);
  border: none;
  border-radius: var(--radius-rounded);
  cursor: pointer;
  transition: opacity 0.15s;

  &:hover {
    opacity: 0.85;
  }
}

.grouping-cancel-button {
  width: 100%;
  padding: 10px;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-normal);
  background-color: var(--color-surface-muted);
  border: none;
  border-radius: var(--radius-rounded);
  cursor: pointer;
  transition: opacity 0.15s;

  &:hover {
    opacity: 0.85;
  }
}
</style>
